import { createContext, useCallback, useContext, useEffect, useMemo, useReducer, useRef, type ReactNode } from 'react';
import type { AppConfig, Theme } from '@avs/contract';
import {
  loadConfig, saveConfig,
  loadTheme, saveTheme,
  loadCollapsed, saveCollapsed,
} from './persistence.js';
import { readRouteFromHash, writeRouteToHash, subscribeHashChange, type RouteKey } from './router.js';

export type AppState = {
  theme: Theme;
  collapsed: boolean;
  route: RouteKey;
  config: AppConfig;
};

type Action =
  | { type: 'setTheme'; theme: Theme }
  | { type: 'toggleCollapsed' }
  | { type: 'setRoute'; route: RouteKey }
  | { type: 'updateConfig'; patch: Partial<AppConfig> };

function reducer(state: AppState, action: Action): AppState {
  switch (action.type) {
    case 'setTheme':       return { ...state, theme: action.theme };
    case 'toggleCollapsed':return { ...state, collapsed: !state.collapsed };
    case 'setRoute':       return { ...state, route: action.route };
    case 'updateConfig':   return { ...state, config: { ...state.config, ...action.patch } };
  }
}

export type AppActions = {
  setTheme: (theme: Theme) => void;
  toggleCollapsed: () => void;
  navigate: (route: RouteKey) => void;
  updateConfig: (patch: Partial<AppConfig>) => void;
};

type Ctx = { state: AppState; actions: AppActions };
const AppCtx = createContext<Ctx | null>(null);

/** 应用主题到 <html>：'system' → 读 prefers-color-scheme */
function applyThemeToDom(theme: Theme): void {
  let isDark = false;
  if (theme === 'dark') isDark = true;
  else if (theme === 'system' && window.matchMedia) {
    isDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
  }
  document.documentElement.classList.toggle('dark', isDark);
}

export function AppProvider({ children }: { children: ReactNode }) {
  // 初始 state 一次性从 localStorage / hash 拉
  const [state, dispatch] = useReducer(reducer, undefined, (): AppState => ({
    theme: loadTheme(),
    collapsed: loadCollapsed(),
    route: readRouteFromHash(),
    config: loadConfig(),
  }));

  // 持久化：单独 effect 跟踪每个字段
  useEffect(() => { saveTheme(state.theme); applyThemeToDom(state.theme); }, [state.theme]);
  useEffect(() => { saveCollapsed(state.collapsed); }, [state.collapsed]);
  useEffect(() => { saveConfig(state.config); }, [state.config]);
  useEffect(() => { writeRouteToHash(state.route); }, [state.route]);

  // 监听 hash 变化（用户后退/前进）—— 防止跟 dispatch 死循环
  const lastRouteRef = useRef(state.route);
  useEffect(() => { lastRouteRef.current = state.route; }, [state.route]);
  useEffect(() => subscribeHashChange(() => {
    const next = readRouteFromHash();
    if (next !== lastRouteRef.current) dispatch({ type: 'setRoute', route: next });
  }), []);

  // 监听 system theme 变化（仅 theme = 'system' 时）
  useEffect(() => {
    if (!window.matchMedia) return;
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () => { if (state.theme === 'system') applyThemeToDom('system'); };
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, [state.theme]);

  const actions = useMemo<AppActions>(() => ({
    setTheme: (theme) => dispatch({ type: 'setTheme', theme }),
    toggleCollapsed: () => dispatch({ type: 'toggleCollapsed' }),
    navigate: (route) => dispatch({ type: 'setRoute', route }),
    updateConfig: (patch) => dispatch({ type: 'updateConfig', patch }),
  }), []);

  const value = useMemo(() => ({ state, actions }), [state, actions]);

  return <AppCtx.Provider value={value}>{children}</AppCtx.Provider>;
}

export function useApp(): Ctx {
  const ctx = useContext(AppCtx);
  if (!ctx) throw new Error('useApp() must be inside <AppProvider>');
  return ctx;
}

/** 便利：只取 state 不要 actions 时 */
export function useAppState(): AppState {
  return useApp().state;
}

// 让 Action 类型在 fast-refresh 时不丢失
// （unused export 但不会被 tree-shake，让编辑器能 jump）
export type { Action as _AppAction };

// avoid `useCallback` lint warning when unused
void useCallback;
